import { useState, useCallback, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { OrganizationsService } from '../services/organizations.service';
import { UseOrganizationsOptions, getOrganizationsQueryKey } from '@shared/types/organization';

export const useOrganizations = (options: UseOrganizationsOptions = {}) => {
  const {
    initialPage = 1,
    initialLimit = 12,
    staleTime = 0,
    refetchOnWindowFocus = false,
    refetchOnMount = true,
  } = options;

  const queryClient = useQueryClient();
  const [page, setPage] = useState(initialPage);
  const [limit, setLimit] = useState(initialLimit);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);

  // Use a ref to track the current page to avoid closure issues
  const currentPageRef = useRef(page);
  currentPageRef.current = page;

  // Base query key without page for invalidation
  const baseQueryKey = ['organizations'];

  // Create a fetch function that we can reuse
  const fetchOrganizations = useCallback(async (pageToFetch: number, limitToUse: number, searchTerm: string) => {
    setLoading(true);
    try {
      console.log(`Fetching organizations for page ${pageToFetch}`);
      return await OrganizationsService.getOrganizations(pageToFetch, limitToUse, searchTerm);
    } finally {
      setLoading(false);
    }
  }, []);

  // Main query
  const {
    data,
    isLoading: isQueryLoading,
    error,
  } = useQuery({
    queryKey: getOrganizationsQueryKey(page, limit, search),
    queryFn: () => fetchOrganizations(page, limit, search),
    staleTime,
    refetchOnWindowFocus,
    refetchOnMount,
  });

  // Refresh mutation
  const { mutate: refresh, isPending: isRefreshing } = useMutation({
    mutationFn: () => fetchOrganizations(currentPageRef.current, limit, search),
    onSuccess: (newData) => {
      queryClient.setQueryData(
        getOrganizationsQueryKey(currentPageRef.current, limit, search),
        newData
      );
    },
  });

  // Handle search
  const handleSearch = useCallback((searchTerm: string) => {
    setSearch(searchTerm);
    setPage(1);
  }, []);

  // Handle page change
  const handlePageChange = useCallback((newPage: number) => {
    // Prevent redundant page changes
    if (newPage === currentPageRef.current) return;

    // Invalidate the current query before changing the page
    queryClient.invalidateQueries({
      queryKey: baseQueryKey,
      exact: false,
      refetchType: 'none',
    });

    // Update the page state
    setPage(newPage);
  }, [queryClient, baseQueryKey]);

  return {
    organizations: data?.organizations ?? [],
    isLoading: isQueryLoading || isRefreshing || loading,
    error: error?.message ?? null,
    page,
    limit,
    total: data?.total ?? 0,
    handleSearch,
    handlePageChange,
    refresh
  };
};
